// Search landing pages. Every claim here comes from existing site copy
// (services, leasing, about, contact); keep it that way when editing.
export type Landing = {
  path: string;
  title: string;
  description: string;
  eyebrow: string;
  heading: string;
  highlight: string;
  lead: string;
  image: { src: string; alt: string };
  primary: { label: string; to: string };
  secondary: { label: string; to: string };
  offerHeading: string;
  offerIntro: string;
  offers: [string, string][];
  faqs: { question: string; answer: string }[];
  related: { label: string; to: string }[];
};

const trust: [string, string][] = [
  ["BGLC-licensed", "Technogenious Solutions Ltd. is licensed by the Betting, Gaming and Lotteries Commission as a technical service provider for gaming machines."],
  ["Pyramid authorised", "We are an authorised sales and service centre for Pyramid Technologies bill validators and ticket printers."],
  ["In Kingston", "Visit us at Shop 4B, 17 Molynes Road, Kingston 10, Monday to Saturday, 9:00 am to 6:00 pm."],
];
export const trustPoints = trust;

export const landings: Landing[] = [
  {
    path: "/gaming-machine-parts-jamaica",
    title: "Gaming machine parts in Jamaica",
    description: "Bill acceptors, ticket printers, touchscreens, LCD monitors, gaming boards and power supplies for gaming machines. Shop online or visit us in Kingston, Jamaica.",
    eyebrow: "TECHNOGENIOUS · GAMING MACHINE PARTS",
    heading: "Gaming machine parts",
    highlight: "in Jamaica.",
    lead: "Replacement parts and accessories for gaming machines, from bill acceptors and ticket printers to power supplies and cables. Free store pickup in Kingston, with local and international delivery.",
    image: { src: "/installations/installation-updated-02.jpg", alt: "Gaming machines installed by Technogenious in Jamaica" },
    primary: { label: "Shop parts →", to: "/products" },
    secondary: { label: "Ask about a part ↗", to: "/contact" },
    offerHeading: "Parts we supply",
    offerIntro: "We source parts and accessories from local and international suppliers to support replacement, repair and assembly projects.",
    offers: [
      ["Bill acceptors & validators", "Pyramid bill validators and other bill acceptors, with advice from an authorised Pyramid sales and service centre."],
      ["Ticket printers", "Printers for ticket-in, ticket-out machines, including Pyramid ticket printers."],
      ["Touchscreens & LCD monitors", "Screens and monitors for replacing worn or damaged displays."],
      ["Gaming boards", "Boards for repairs, upgrades and new machine builds."],
      ["Power supplies", "Power supplies to get a machine running reliably again."],
      ["Cables, switches & components", "Cables, switches and electronic components for repairs and assembly."],
    ],
    faqs: [
      { question: "How do I check a part fits my machine?", answer: "Send us your gaming machine brand, model and the part number, with a clear photo if you can. Our team will check compatibility before you order." },
      { question: "Can I collect my order in Kingston?", answer: "Yes. Store pickup at Shop 4B, 17 Molynes Road, Kingston 10 is free. Local and international delivery are also available at checkout." },
      { question: "Do you service Pyramid bill acceptors and printers?", answer: "Yes. We are an authorised sales and service centre for Pyramid Technologies bill validators and ticket printers. Include the model and serial number with your enquiry." },
    ],
    related: [
      { label: "Gaming machine repairs & maintenance", to: "/gaming-machine-repair-kingston" },
      { label: "Gaming machines for sale", to: "/gaming-machines-for-sale-jamaica" },
      { label: "Browse all products", to: "/products" },
    ],
  },
  {
    path: "/gaming-machines-for-sale-jamaica",
    title: "Gaming machines for sale & lease in Jamaica",
    description: "Buy, lease or build gaming machines in Jamaica, including roulette and slot games. Licensed installation and maintenance from Technogenious in Kingston.",
    eyebrow: "TECHNOGENIOUS · GAMING MACHINES",
    heading: "Gaming machines for sale",
    highlight: "and lease in Jamaica.",
    lead: "Buy gaming machines outright, lease them for your venue on a profit share, or build your own configuration. We manufacture, install and maintain machines across Jamaica.",
    image: { src: "/installations/installation-updated-05.jpg", alt: "American Roulette and Pot O’ Gold gaming machines installed side by side" },
    primary: { label: "Explore our machines →", to: "/gaming-machines" },
    secondary: { label: "Build your machine ↗", to: "/build-your-machine" },
    offerHeading: "Three ways to get machines",
    offerIntro: "Choose the arrangement that suits your business. Our team confirms the game, hardware, quantity, installation and delivery in your quotation.",
    offers: [
      ["Buy", "Purchase machines for your premises. Installation for purchased machines is available as a paid service."],
      ["Lease", "Licensed bars and other eligible operators can lease our machines on a 60/40 profit share: your venue keeps 40%. We install and maintain leased machines."],
      ["Build to order", "Pick a game, then choose the cabinet, monitor, bill acceptor, printer and other hardware. We manufacture and assemble to your agreed specification."],
    ],
    faqs: [
      { question: "Who can lease gaming machines?", answer: "Leasing is for licensed bars and other eligible operators. Tell us your venue type, parish, available space and how many machines you are considering." },
      { question: "How does the profit share work?", answer: "Profits from leased machines are shared 40% to your venue and 60% to Technogenious. Leased machines remain our property, and we configure, install and maintain them. All terms are confirmed in your agreement." },
      { question: "Can I choose the game and hardware?", answer: "Yes. Use the machine builder to pick a game and review the standard configuration, then send it to our team to discuss upgrades and get a quotation." },
    ],
    related: [
      { label: "Gaming machine leasing", to: "/leasing" },
      { label: "Gaming machine parts", to: "/gaming-machine-parts-jamaica" },
      { label: "Request a quote", to: "/request-quote" },
    ],
  },
  {
    path: "/gaming-machine-repair-kingston",
    title: "Gaming machine repair in Kingston, Jamaica",
    description: "Gaming machine repairs, fault diagnosis, installation and maintenance from a BGLC-licensed technical service provider at 17 Molynes Road, Kingston.",
    eyebrow: "TECHNOGENIOUS · REPAIRS & MAINTENANCE",
    heading: "Gaming machine repair",
    highlight: "in Kingston, Jamaica.",
    lead: "Fault diagnosis, repairs, installation and ongoing maintenance for gaming machines, from a BGLC-licensed technical service provider with a parts counter on site.",
    image: { src: "/installations/installation-updated-03.jpg", alt: "A gaming machine installation serviced by Technogenious" },
    primary: { label: "Describe your fault →", to: "/request-quote" },
    secondary: { label: "Our technical services ↗", to: "/services" },
    offerHeading: "How we can help",
    offerIntro: "Tell us the machine model, describe the fault and include any error messages so we can assess the problem and explain what parts or servicing it may need.",
    offers: [
      ["Fault diagnosis", "We work out what is wrong and explain the repair, parts and cost before work goes ahead."],
      ["Repairs", "Repairs to gaming machines and components, with replacement parts from our own stock where needed."],
      ["Installation", "Installation of new and purchased machines at your premises, available as a paid service."],
      ["Maintenance", "Ongoing maintenance to keep your machines running and earning."],
      ["Pyramid servicing", "Advice and servicing for Pyramid bill validators and ticket printers, as an authorised service centre."],
    ],
    faqs: [
      { question: "What should I send when my machine has a fault?", answer: "Your gaming machine brand and model, a description of the problem and any error messages, clear photos, and your location and contact details." },
      { question: "Are you licensed to work on gaming machines?", answer: "Yes. Technogenious Solutions Ltd. is licensed by the Betting, Gaming and Lotteries Commission as a technical service provider for gaming machines." },
      { question: "Where are you located?", answer: "Shop 4B, 17 Molynes Road, Kingston 10. We are open Monday to Saturday, 9:00 am to 6:00 pm. Call +1 (876) 648-4163 or WhatsApp +1 (876) 448-2032." },
    ],
    related: [
      { label: "Gaming machine parts", to: "/gaming-machine-parts-jamaica" },
      { label: "Warranties", to: "/warranties" },
      { label: "Contact us", to: "/contact" },
    ],
  },
  {
    path: "/bill-acceptors-jamaica",
    title: "Bill acceptors & validators in Jamaica",
    description: "Pyramid bill validators and bill acceptor parts, sold and serviced by an authorised Pyramid centre in Kingston. Shop online or visit 17 Molynes Road.",
    eyebrow: "TECHNOGENIOUS · BILL ACCEPTORS",
    heading: "Bill acceptors",
    highlight: "in Jamaica.",
    lead: "Bill validators, cash boxes and bill acceptor accessories for gaming machines, from an authorised Pyramid Technologies sales and service centre in Kingston.",
    image: { src: "/installations/installation-updated-01.jpg", alt: "Gaming machines with bill acceptors installed by Technogenious in Jamaica" },
    primary: { label: "Shop bill acceptors →", to: "/shop/bill-acceptors-and-accessories" },
    secondary: { label: "Ask about a bill acceptor ↗", to: "/contact" },
    offerHeading: "Bill acceptors and support",
    offerIntro: "Send us the machine model and the bill acceptor's model and serial number, and we'll help you find the right unit or part.",
    offers: [
      ["Pyramid bill validators", "Sold through an authorised Pyramid Technologies sales and service centre."],
      ["Cash boxes & accessories", "Cash boxes and other bill acceptor accessories for gaming machines."],
      ["Servicing & repairs", "Bill validator repairs carry a 30-day repair warranty."],
      ["Compatibility checks", "We check a unit or part suits your machine before you order."],
    ],
    faqs: [
      { question: "How do I know which bill acceptor fits my machine?", answer: "Send us your gaming machine brand and model, plus the model and serial number of the bill acceptor you have now, with a clear photo if you can. Our team will confirm what fits." },
      { question: "Do you repair bill acceptors?", answer: "Yes. We are an authorised Pyramid Technologies service centre. Repairs to gaming components, including bill validators, carry a 30-day repair warranty." },
      { question: "Can I collect in Kingston?", answer: "Yes. Store pickup at Shop 4B, 17 Molynes Road, Kingston 10 is free. Local and international delivery are also available at checkout." },
    ],
    related: [
      { label: "Ticket printers", to: "/ticket-printers-jamaica" },
      { label: "Pyramid service centre", to: "/pyramid-service-centre-kingston" },
      { label: "Gaming machine parts", to: "/gaming-machine-parts-jamaica" },
    ],
  },
  {
    path: "/ticket-printers-jamaica",
    title: "Gaming machine ticket printers in Jamaica",
    description: "Ticket printers, printer paper and accessories for ticket-in, ticket-out gaming machines, from an authorised Pyramid centre in Kingston, Jamaica.",
    eyebrow: "TECHNOGENIOUS · TICKET PRINTERS",
    heading: "Ticket printers",
    highlight: "in Jamaica.",
    lead: "Printers, thermal printer paper and accessories for ticket-in, ticket-out gaming machines, including Pyramid ticket printers, sold and serviced in Kingston.",
    image: { src: "/installations/installation-updated-04.jpg", alt: "A gaming machine installation by Technogenious in Jamaica" },
    primary: { label: "Shop printers →", to: "/shop/printers-and-accessories" },
    secondary: { label: "Ask about a printer ↗", to: "/contact" },
    offerHeading: "Printers and support",
    offerIntro: "Tell us the machine model and the printer's model and serial number, and we'll help you find the right printer, paper or part.",
    offers: [
      ["Ticket printers", "Printers for ticket-in, ticket-out machines, including Pyramid ticket printers."],
      ["Printer paper & accessories", "Thermal printer paper and printer accessories."],
      ["Servicing & repairs", "Printer repairs carry a 30-day repair warranty."],
      ["Compatibility checks", "We check a printer or part suits your machine before you order."],
    ],
    faqs: [
      { question: "Which printer works with my machine?", answer: "Send us your gaming machine brand and model, plus the model and serial number of your current printer, with a clear photo if you can. Our team will confirm what fits." },
      { question: "Do you repair ticket printers?", answer: "Yes. We are an authorised Pyramid Technologies service centre. Repairs to gaming components, including printers, carry a 30-day repair warranty." },
      { question: "Do you sell printer paper?", answer: "Yes. Thermal printer paper is in the shop, with free store pickup in Kingston and local and international delivery." },
    ],
    related: [
      { label: "Bill acceptors", to: "/bill-acceptors-jamaica" },
      { label: "Pyramid service centre", to: "/pyramid-service-centre-kingston" },
      { label: "Gaming machine parts", to: "/gaming-machine-parts-jamaica" },
    ],
  },
  {
    path: "/pyramid-service-centre-kingston",
    title: "Pyramid sales & service centre in Kingston",
    description: "Authorised Pyramid Technologies sales and service centre in Kingston, Jamaica, for bill validators and ticket printers. Shop 4B, 17 Molynes Road.",
    eyebrow: "TECHNOGENIOUS · PYRAMID TECHNOLOGIES",
    heading: "Pyramid sales & service",
    highlight: "in Kingston.",
    lead: "Technogenious Solutions is an authorised sales and service centre for Pyramid Technologies bill validators and ticket printers. Buy, repair or get advice at 17 Molynes Road.",
    image: { src: "/installations/installation-updated-03.jpg", alt: "A gaming machine installation serviced by Technogenious" },
    primary: { label: "Request service →", to: "/request-quote" },
    secondary: { label: "Shop bill acceptors ↗", to: "/shop/bill-acceptors-and-accessories" },
    offerHeading: "What we do for Pyramid equipment",
    offerIntro: "Bring your unit to Shop 4B, 17 Molynes Road, Kingston 10, or send the model, serial number and a description of the fault before you visit.",
    offers: [
      ["Bill validator sales", "Pyramid bill validators, sold by an authorised sales centre."],
      ["Ticket printer sales", "Pyramid ticket printers for ticket-in, ticket-out machines."],
      ["Servicing & repairs", "Repairs to bill validators and printers carry a 30-day repair warranty."],
      ["Advice", "Help choosing and fitting the right unit for your machine."],
    ],
    faqs: [
      { question: "What should I bring or send?", answer: "The unit's model and serial number, your gaming machine brand and model, a description of the fault and any error messages. Clear photos help." },
      { question: "Is the repair guaranteed?", answer: "Repairs to gaming components, including bill validators and printers, carry a 30-day repair warranty. See our warranties page for the full terms." },
      { question: "When are you open?", answer: "Monday to Saturday, 9:00 am to 6:00 pm, at Shop 4B, 17 Molynes Road, Kingston 10. Call +1 (876) 648-4163 or WhatsApp +1 (876) 448-2032." },
    ],
    related: [
      { label: "Bill acceptors", to: "/bill-acceptors-jamaica" },
      { label: "Ticket printers", to: "/ticket-printers-jamaica" },
      { label: "Warranties", to: "/warranties" },
    ],
  },
];
